import { typeGuardAssertion } from '../../services/apiUtils';

type DateString = string;

export type TradingGateway = 'fusion' | 'inet' | 'geniuminet';

interface IOrderRecordKeeping {
    client_id: string | null;
    client_type: string | null;
    investment_decision_id: string | null;
    investment_decision_type: string | null;
    executing_trader_id: string | null;
    executing_trader_type: string | null;
}

export interface IPrivateOrder {
    trading_gateway: TradingGateway;
    comp_id: string;
    id: string;
    exchange_order_id: string | null;
    exec_id: string;
    exec_date: DateString
    symbol: string;
    exchange_cl_ord_id: string | null;
    time_in_force: string;
    type: string;
    price: number | null;
    quantity: number;
    value: number | null;
    minimum_quantity: number | null;
    filled_quantity: number;
    remaining_quantity: number;
    visible_quantity: number | null;
    display_range: number | null;
    num_trades: number;
    side: string;
    status: string;
    trader_id: string;
    capacity: string;
    liquidity_provision: boolean;
    self_match_prevention_id: string | null;
    self_trade_prevention_level: string | null;
    self_trade_prevention_action: string | null;
    self_trade_prevention_trader_group: string | null;
    peg_type: string | null;
    peg_difference: string | null;
    created_at: DateString | null;
    last_updated: DateString | null;
    last_updated_by: string;
    last_price: number | null;
    average_price: number | null;
    last_quantity: number | null;
    market_feed_id: string | null;
    vwap: number | null;
    expires_at: DateString | null;
    action_reject_reason: string | null;
    cancel_reason: string | null;
    routing_strategy: string | null;
    clearing_firm: string | null;
    clearing_account: string | null;
    clearing_account_type: string | null;
    record_keeping: IOrderRecordKeeping;
}

export type OrderStatus =
'New'
| 'PendingCancel'
| 'Replaced'
| 'Cancelled'
| 'Rejected'
| 'Filled'
| 'Expired'
| 'PartiallyFilled'
| 'PendingNew';

export type PrivateOrderTickData = IPrivateOrder & {
    tickdata_type: 'PrivateOrder',
    update_code: OrderStatus,
    timestamp: string
    // Change side from bid ask to buy sell?
    // Same with statuses
}

export interface IPrivateTrade {
    trading_gateway: TradingGateway;
    comp_id: string;
    order_id: string;
    symbol: string;
    side: string;
    price: number;
    quantity: number;
    value: number;
    visible_quantity: string | null;
    trade_id: string;
    market_feed_id: string | null;
    exec_id: string;
    counterparty: string | null;
    timestamp: DateString;
    cancelled: string;
    cancelled_at: DateString | null;
    replaced_at: DateString | null;
    orders: Array<IPrivateOrder>
}

export type PrivateTradeTickData = IPrivateTrade & {
    tickdata_type: 'PrivateTrade',
    update_code: 'Execution'
}

export interface IPrivateInetTradeReport {
    trading_gateway: 'inet';
    id: string;
    status: string;
    reason: string | null;
    is_initiated: boolean;
    symbol: string | null;
    price: number;
    quantity: number;
    value: number;
    settlement_date: DateString | null;
    timestamp: DateString | null;
    trade_type: string | null;
    trade_registration_timestamp: DateString;
    trade_publish_indicator: string | null;
    trade_clearing_instruction: string | null;
    execution_method: string | null;
    side: string;
    capacity: string;
    is_internal: boolean;
    trade_id: string | null;
    counterparty: string | null;
    counterparty_capacity: string | null;
    executing_firm: string | null;
    trader_id: string | null;
    comp_id: string;
    deal_id: string | null;
    last_updated: DateString;
    last_updated_by: string | null;
    match_id: string | null;
    record_keeping: IOrderRecordKeeping | null;
    reject_reason: string | null;
    trade_report_id: string | null;
    trade_report_ref_id: string | null;
}
export interface IPrivateGeniumInetTradeReport {
    trading_gateway: 'fusion';
    id: string;
    status: string;
    reason: string | null;
    is_initiated: boolean;
    symbol: string | null;
    price: number;
    quantity: number;
    value: number;
    settlement_date: DateString | null;
    timestamp: DateString | null;
    trade_type: string | null;
    trade_registration_timestamp: DateString | null;
    trade_publish_indicator: string | null;
    trade_clearing_instruction: string | null;
    execution_method: string | null;
    side: string;
    capacity: string;
    is_internal: boolean;
    trade_id: string | null;
    counterparty: string | null;
    counterparty_capacity: string | null;
    executing_firm: string | null;
    trader_id: string | null;
    comp_id: string;
    deal_id: string | null;
    last_updated: DateString | null;
    last_updated_by: string | null;
    match_id: string | null;
    record_keeping: IOrderRecordKeeping | null;
    reject_reason: string | null;
    trade_report_id: string | null;
    trade_report_ref_id: string | null;
}

export type TradeReportStatus =
'TRReceivedU'
| 'TRSentU'
| 'TRReject'
| 'TRCancel'
| 'TRMatched'
| 'TRSentB'
| 'TRReceivedB'
| 'TRBroken';

export type PrivateTradeReportTickData = (IPrivateInetTradeReport | IPrivateGeniumInetTradeReport) & {
    timestamp: string,
    tickdata_type: 'PrivateTradeReport'
    update_code: TradeReportStatus,
}

export type PrivateDataError = {
    error: string
}

export const isPrivateDataError = (obj: any): obj is PrivateDataError => {
    return typeGuardAssertion<PrivateDataError>(obj, {
        error: { type: 'string' },
        systemProfileEndpointId: { type: 'string' },
    });
}
export const isPrivateDataErrorSimple = (obj: any): obj is PrivateDataError => {
    return typeGuardAssertion<PrivateDataError>(obj, {
        error: { type: 'string' },
    });
}

export const ORDER_STATUS_MAP: {
    [key: string]: string
} = {
    'new': 'New',
    'pending_cancel': 'PendingCancel',
    'replaced': 'Replaced',
    'cancelled': 'Cancelled',
    'rejected': 'Rejected',
    'filled': 'Filled',
    'expired': 'Expired',
    'partially_filled': 'PartiallyFilled',
    'pending_new': 'PendingNew'
}

export const TRADE_REPORT_STATUS_MAP : {
    [key: string]: string
} = {
    'received_unmatched': 'TRReceivedU',
    'sent_unmatched': 'TRSentU',
    'rejected': 'TRReject',
    'cancelled': 'TRCancel',
    'matched': 'TRMatched',
    'sent_break': 'TRSentB',
    'received_break': 'TRReceivedB',
    'broken': 'TRBroken'
};
